/**
 * The forced setup wizard.
 *
 * A workspace that has not finished being set up cannot be used, so this replaces the whole
 * application rather than sitting inside it: no sidebar, no topbar, nowhere else to go. Offering a
 * menu of destinations that would every one of them refuse is worse than offering none, and the
 * refusals would arrive one at a time with no explanation of what to do about them.
 *
 * The order is the order the requirements depend on each other in:
 *
 *   1. Two-step verification.  Everything below is a protected action, and a protected action needs
 *      something to step up with. Enrolment happens here rather than through a link to account
 *      security, because that page is inside the application this screen is standing in front of.
 *   2. The subscription.       Paid before the workspace opens, in Stripe, on the package the
 *      invitation was issued for.
 *   3. Stripe Connect.         Taking a customer's money needs somewhere for it to go. Charges are
 *      the requirement; payouts are reported but never blocking, because Stripe enables those on
 *      its own review timeline and nobody here can hurry it.
 *   4. The first venue.        Offered, never required. A workspace is lawful and operable without
 *      one, and adding venues is ordinary in-app work.
 *
 * Nothing on this screen decides whether setup is finished. Every requirement is recomputed by the
 * server from the database, and the browser only asks it to look again. See
 * edge/api/src/modules/tenancy/onboarding.ts.
 */

import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, jsonBody } from "../../api/client";
import type { ListResponse } from "../../api/types";
import {
  type OnboardingStage,
  type OnboardingStep,
  useRefreshWorkspaceAccess,
  useWorkspaceAccessQuery,
} from "../../app/workspaceAccess";
import { useAuth } from "../../auth/AuthProvider";
import { isOwner as membershipIsOwner } from "../../auth/permissions";
import { useSensitiveAction } from "../../auth/SensitiveActionGate";
import { BrandMark } from "../../brand/BrandMark";
import { formatMoney, titleCase } from "../../lib/format";
import { PRODUCT_NAME } from "../../product";
import { Card, CardHeader, ErrorPanel, Field, FormActions, Notice } from "../../ui/components";
import { BoundarySkeleton } from "../../ui/skeletons";

interface MFAStatus {
  enabled: boolean;
  recoveryCodesRemaining: number;
  recentlyVerified: boolean;
  verifiedUntil?: string;
}

interface Enrollment {
  secret: string;
  otpauthUrl: string;
  period: number;
  digits: number;
}

interface RecoveryCodes {
  recoveryCodes: string[];
}

interface Subscription {
  status: string;
  planCode: string;
  billingInterval: "monthly" | "annual";
  pendingPlanCode?: string;
  pendingBillingInterval?: "monthly" | "annual";
  introductoryTrialDays: number;
  billingEmail: string;
  currency: string;
}

interface ConnectedAccount {
  state: string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  disabledReason?: string;
}

interface BillingStatus {
  subscription: Subscription;
  connectedAccount: ConnectedAccount;
  entitlementState: string;
}

interface Plan {
  code: string;
  name: string;
  description: string;
  monthlyPriceMinor: number;
  annualPriceMinor: number;
  currency: string;
}

interface Redirect {
  url: string;
}

const stepTitles: Record<OnboardingStage, string> = {
  owner_security: "Secure the owner account",
  billing: "Confirm the subscription",
  configuration: "Connect payments",
  venue_setup: "Add your first venue",
  ready: "Ready",
};

/**
 * Stripe Connect is available in far more places than this, but a select of two hundred entries is a
 * worse question than a short list plus the account's own country arriving from Stripe afterwards.
 * Anywhere missing is one support message away, and the country is only ever set once.
 */
const connectCountries = [
  ["GB", "United Kingdom"],
  ["IE", "Ireland"],
  ["US", "United States"],
  ["CA", "Canada"],
  ["AU", "Australia"],
  ["NZ", "New Zealand"],
  ["FR", "France"],
  ["DE", "Germany"],
  ["ES", "Spain"],
  ["IT", "Italy"],
  ["NL", "Netherlands"],
  ["BE", "Belgium"],
  ["PT", "Portugal"],
  ["SE", "Sweden"],
  ["NO", "Norway"],
  ["DK", "Denmark"],
  ["FI", "Finland"],
  ["PL", "Poland"],
  ["CH", "Switzerland"],
  ["AT", "Austria"],
] as const;

function StepList({ steps, current }: { steps: OnboardingStep[]; current: OnboardingStage }) {
  return (
    <ol className="gate-steps">
      {steps.map((step, index) => {
        const state = step.satisfied ? "done" : step.key === current ? "current" : "waiting";
        return (
          <li className={`gate-step gate-step-${state}`} key={step.key}>
            <span className="gate-step-mark" aria-hidden="true">
              {step.satisfied ? "✓" : index + 1}
            </span>
            <span className="gate-step-body">
              <strong>{stepTitles[step.key]}</strong>
              <small>
                {step.satisfied
                  ? "Done"
                  : step.required
                    ? (step.note ?? "Required before the workspace opens")
                    : (step.note ?? "Optional")}
              </small>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export function OnboardingWizard() {
  const auth = useAuth();
  const access = useWorkspaceAccessQuery();
  const refreshAccess = useRefreshWorkspaceAccess();
  const [searchParams] = useSearchParams();
  const owner = membershipIsOwner(auth.session?.selectedMembership);
  const sensitive = useSensitiveAction({
    lockedTitle: "Confirm it is you",
    lockedDescription:
      "Enter a current authenticator code. Payment and Stripe setup are protected actions, and the confirmation lasts fifteen minutes.",
  });

  const mfa = useQuery({
    queryKey: ["auth", "mfa"],
    queryFn: () => api<MFAStatus>("/api/v1/auth/mfa"),
  });
  const billing = useQuery({
    queryKey: ["billing", "status"],
    queryFn: () => api<BillingStatus>("/api/v1/billing/status"),
    enabled: owner,
    // The subscription becomes active through a Stripe webhook rather than through anything this
    // page does, so returning from checkout means waiting for an answer that arrives on its own.
    refetchInterval: owner ? 3_000 : false,
  });
  const plans = useQuery({
    queryKey: ["public", "billing-plans"],
    queryFn: () => api<ListResponse<Plan>>("/api/v1/billing/plans"),
  });

  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [country, setCountry] = useState("GB");
  const [working, setWorking] = useState("");
  const [error, setError] = useState<unknown>();

  const status = access.data?.onboarding;
  const currentStep = status?.currentStep ?? "owner_security";

  // Recording progress is the server's decision, asked for rather than asserted: the request carries
  // no body at all. It runs whenever the stage the workspace has actually reached is ahead of the
  // stage stored on it, which is what closes this screen once the last requirement is met.
  //
  // At most one attempt per distinct transition. The request is followed by a re-read, and a re-read
  // that comes back saying the same thing would otherwise re-run this immediately and forever: a
  // server that declines to move the stage would be asked again on every render.
  const attempted = useRef("");
  const storedStage = status?.state ?? "";
  useEffect(() => {
    if (!status || !owner || storedStage === status.currentStep) return;
    const transition = `${storedStage}->${status.currentStep}`;
    if (attempted.current === transition) return;
    attempted.current = transition;
    api<unknown>("/api/v1/tenant/onboarding", { method: "PUT" })
      .then(() => refreshAccess())
      .catch(() => undefined);
  }, [status, storedStage, owner, refreshAccess]);

  // Stripe sends people back here after Connect. Asking Stripe for the account once on return means
  // the answer is current without anybody having to press anything.
  const syncedOnReturn = useRef(false);
  useEffect(() => {
    if (!owner || syncedOnReturn.current || !searchParams.has("connect")) return;
    syncedOnReturn.current = true;
    api<unknown>("/api/v1/billing/connect/sync", { method: "POST" })
      .then(() => Promise.all([billing.refetch(), refreshAccess()]))
      .catch(() => undefined);
  }, [owner, searchParams, billing, refreshAccess]);

  if (access.isLoading || mfa.isLoading) return <BoundarySkeleton />;

  const subscription = billing.data?.subscription;
  const connected = billing.data?.connectedAccount;
  const planCode = subscription?.pendingPlanCode || subscription?.planCode || "";
  const billingInterval =
    subscription?.pendingBillingInterval || subscription?.billingInterval || "monthly";
  const plan = plans.data?.items.find((item) => item.code === planCode);
  const priceMinor =
    billingInterval === "annual" ? plan?.annualPriceMinor : plan?.monthlyPriceMinor;
  const checkoutResult = searchParams.get("subscription");

  function begin(label: string) {
    setWorking(label);
    setError(undefined);
  }

  async function beginEnrollment() {
    begin("enrollment");
    try {
      setEnrollment(
        await api<Enrollment>("/api/v1/auth/mfa/enrollment", {
          method: "POST",
          body: jsonBody({}),
        }),
      );
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking("");
    }
  }

  async function confirmEnrollment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    begin("enrollment");
    try {
      const result = await api<RecoveryCodes>("/api/v1/auth/mfa/enrollment/confirm", {
        method: "POST",
        body: jsonBody({ code: String(form.get("code") ?? "") }),
      });
      setRecoveryCodes(result.recoveryCodes);
      setEnrollment(null);
      await Promise.all([mfa.refetch(), auth.refresh(), refreshAccess()]);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking("");
    }
  }

  async function startCheckout() {
    if (!planCode) return;
    begin("checkout");
    try {
      const redirect = await api<Redirect>("/api/v1/billing/subscription-checkout", {
        method: "POST",
        body: jsonBody({ planCode, billingInterval }),
      });
      window.location.assign(redirect.url);
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
      setWorking("");
    }
  }

  async function startConnect() {
    begin("connect");
    try {
      const redirect = await api<Redirect>("/api/v1/billing/connect/onboarding", {
        method: "POST",
        body: jsonBody({ country }),
      });
      window.location.assign(redirect.url);
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
      setWorking("");
    }
  }

  async function syncConnect() {
    begin("connect-sync");
    try {
      await api<unknown>("/api/v1/billing/connect/sync", { method: "POST" });
      await Promise.all([billing.refetch(), refreshAccess()]);
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking("");
    }
  }

  return (
    <main className="gate-layout">
      <div className="gate-shell stack">
        <header className="gate-header">
          <BrandMark />
          <div>
            <h1>
              Finish setting up {auth.session?.selectedMembership?.tenantName ?? PRODUCT_NAME}
            </h1>
            <p>
              These few things have to be settled before the workspace opens. Everything else can
              wait until you are inside.
            </p>
          </div>
        </header>

        {error ? <ErrorPanel error={error} /> : null}
        {access.error ? <ErrorPanel error={access.error} /> : null}
        {owner && billing.error ? <ErrorPanel error={billing.error} /> : null}

        {status ? <StepList steps={status.steps} current={currentStep} /> : null}

        {!owner ? (
          <Notice tone="warning" title="The workspace owner has to finish this">
            Payment and payout setup belong to the person who owns this workspace. Ask them to sign
            in and complete these steps; everything opens for everybody as soon as they do.
          </Notice>
        ) : null}

        {recoveryCodes.length > 0 ? (
          <Notice tone="success" title="Save your recovery codes now">
            <span className="mono">{recoveryCodes.join("  ")}</span>
          </Notice>
        ) : null}

        <Card>
          <CardHeader
            title={`1. ${stepTitles.owner_security}`}
            description="Two-step verification protects payment, payouts and everything else this account can change."
          />
          {mfa.data?.enabled ? sensitive.view : null}
          {!mfa.data?.enabled && !enrollment ? (
            <div className="stack">
              <Notice tone="warning">
                Set up an authenticator app before continuing. It takes about a minute.
              </Notice>
              <button
                className="button primary"
                type="button"
                disabled={working === "enrollment"}
                onClick={beginEnrollment}
              >
                {working === "enrollment" ? "Working..." : "Set up two-step verification"}
              </button>
            </div>
          ) : null}
          {enrollment ? (
            <form className="form-stack" onSubmit={confirmEnrollment}>
              <Notice tone="info" title="Add this account">
                <span>
                  Open <a href={enrollment.otpauthUrl}>the setup link</a> in your authenticator app,
                  or enter this key: <strong className="mono">{enrollment.secret}</strong>. The app
                  makes a new {enrollment.digits}-digit code every {enrollment.period} seconds.
                </span>
              </Notice>
              <Field label="Authenticator code">
                <input
                  name="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                />
              </Field>
              <FormActions
                submitting={working === "enrollment"}
                submitLabel="Confirm and enable"
                onCancel={() => setEnrollment(null)}
              />
            </form>
          ) : null}
        </Card>

        {owner ? (
          <Card>
            <CardHeader
              title={`2. ${stepTitles.billing}`}
              description="The package and interval were set by the invitation and cannot be changed during checkout."
            />
            {checkoutResult === "success" && subscription?.status !== "active" ? (
              <Notice tone="info" title="Waiting for Stripe">
                Stripe has sent you back. The workspace opens once a verified webhook confirms the
                payment, which is usually a few seconds. This page is watching for it.
              </Notice>
            ) : null}
            {checkoutResult === "cancelled" ? (
              <Notice tone="warning" title="Checkout was not completed">
                Nothing was charged. You can go back to Stripe whenever you are ready.
              </Notice>
            ) : null}
            <dl className="definition-list">
              <dt>Package</dt>
              <dd>{plan?.name ?? (planCode ? titleCase(planCode) : "Confirmed in Stripe")}</dd>
              <dt>Billing interval</dt>
              <dd>{titleCase(billingInterval)}</dd>
              <dt>Price</dt>
              <dd>
                {priceMinor === undefined
                  ? "Confirmed in Stripe"
                  : `${formatMoney(priceMinor, plan?.currency ?? subscription?.currency ?? "USD")} / ${
                      billingInterval === "annual" ? "year" : "month"
                    }`}
              </dd>
              <dt>Billing contact</dt>
              <dd>{subscription?.billingEmail ?? ""}</dd>
              <dt>Introductory offer</dt>
              <dd>
                {subscription?.introductoryTrialDays
                  ? `${subscription.introductoryTrialDays} days before the first charge`
                  : "Standard billing starts at checkout"}
              </dd>
            </dl>
            {subscription?.status === "active" ? (
              <Notice tone="success" title="Subscription confirmed">
                Stripe has confirmed the {plan?.name ?? titleCase(planCode)} package.
              </Notice>
            ) : (
              <button
                className="button primary"
                type="button"
                disabled={working === "checkout" || !sensitive.unlocked || !subscription}
                onClick={startCheckout}
              >
                {working === "checkout" ? "Opening Stripe..." : "Continue securely to Stripe"}
              </button>
            )}
          </Card>
        ) : null}

        {owner ? (
          <Card>
            <CardHeader
              title={`3. ${stepTitles.configuration}`}
              description="Your customers pay you directly through Stripe. This is the account their money lands in."
            />
            {connected?.chargesEnabled ? (
              <Notice
                tone={connected.payoutsEnabled ? "success" : "info"}
                title={
                  connected.payoutsEnabled
                    ? "Payments and payouts are live"
                    : "Payments are live, payouts are still under review"
                }
              >
                {connected.payoutsEnabled
                  ? "You can take payments and Stripe will pay them out on its normal schedule."
                  : "You can take payments now. Stripe reviews payouts on its own timeline, and setup is not held up waiting for it."}
              </Notice>
            ) : (
              <div className="stack">
                {connected?.disabledReason ? (
                  <Notice tone="warning" title="Stripe needs more from this account">
                    {connected.disabledReason.replaceAll("_", " ")}
                  </Notice>
                ) : null}
                <Field
                  label="Country of your business"
                  hint="This cannot be changed once Stripe has the account."
                >
                  <select
                    value={country}
                    disabled={Boolean(connected?.state && connected.state !== "not_started")}
                    onChange={(event) => setCountry(event.target.value)}
                  >
                    {connectCountries.map(([code, label]) => (
                      <option key={code} value={code}>
                        {label}
                      </option>
                    ))}
                  </select>
                </Field>
                <div className="inline">
                  <button
                    className="button primary"
                    type="button"
                    disabled={
                      working !== "" || !sensitive.unlocked || subscription?.status !== "active"
                    }
                    onClick={startConnect}
                  >
                    {working === "connect" ? "Opening Stripe..." : "Set up payments with Stripe"}
                  </button>
                  <button
                    className="button secondary"
                    type="button"
                    disabled={working !== "" || !sensitive.unlocked}
                    onClick={syncConnect}
                  >
                    {working === "connect-sync" ? "Checking..." : "I have finished in Stripe"}
                  </button>
                </div>
                {subscription?.status !== "active" ? (
                  <small>Available once the subscription is confirmed.</small>
                ) : null}
              </div>
            )}
          </Card>
        ) : null}

        <Card>
          <CardHeader
            title={`4. ${stepTitles.venue_setup}`}
            description="Optional. The workspace opens without one, and venues and spaces can be added at any time."
          />
          <p>
            Nothing to do here yet. As soon as the steps above are done this screen closes and the
            application opens, with venue setup waiting for you inside.
          </p>
        </Card>

        <div className="inline">
          <button
            className="button ghost"
            type="button"
            onClick={() => {
              void auth.signOut().then(() => window.location.replace("/login"));
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </main>
  );
}
